/**
 * Roster gate policy layer — the single chokepoint where applicability and
 * tenant enforcement are applied to detected findings.
 *
 * Detection answers "is this true, and how bad is this instance".
 * This answers "does it apply here, and what is the most it may weigh".
 *
 * Invariants (all asserted in roster-gate-policy.test.ts):
 *   I1  Nothing is ever dropped. Every candidate in, one decision out.
 *   I2  Policy can only LOWER severity, never raise it — EXCEPT for I3.
 *   I3  neverSoften (NON_NEGOTIABLE) rules ignore tenant mode and are FLOORED
 *       to their enforced severity. This is the one deliberate exception to
 *       I2, because a non-overridable rule persisted below BLOCK is invisible
 *       to both the publish RPC and roster_publish_rule_is_non_overridable().
 *   I4  Unknown rule codes and unknown modes fail CLOSED (treated as enforcing).
 */

import {
  gateApplicability,
  severityAtMost,
  type GateSeverity,
  type GateServiceModel,
  type GateGoverningMode,
} from './roster-gate-registry';

export type TenantGateProfile = {
  readonly tenantId: string;
  /** Tenant-level default. Per-candidate serviceModel overrides it for MIXED. */
  readonly serviceModel: GateServiceModel | 'MIXED';
  readonly modes: Partial<Record<GateGoverningMode, string | null>>;
  /**
   * Optional tenant-configured ceilings for rules that are advisory during
   * staged adoption. An omitted map means the registry's normal ceilings are
   * used. The caller must pass an explicit map when a compatibility default is
   * required; the policy engine never infers one from tenant identity.
   */
  readonly advisoryCeilings?: Readonly<Partial<Record<string, GateSeverity>>>;
};

/**
 * Compatibility configuration for the existing no-friction roster posture.
 * It is provider-agnostic and must be passed by the runtime caller when that
 * posture is intended. Passing `{}` opts a tenant into the registry ceilings.
 */
export const DEFAULT_ROSTER_GATE_ADVISORY_CEILINGS = Object.freeze({
  WORKER_ON_LEAVE: 'INFO',
  BROKEN_SHIFT_SPAN_EXCEEDED: 'INFO',
} satisfies Partial<Record<string, GateSeverity>>);

export type GateCandidate = {
  readonly ruleCode: string;
  readonly severity: GateSeverity;
  /** Resolved per shift/slot. Required when the tenant is MIXED. */
  readonly serviceModel?: GateServiceModel;
  readonly [key: string]: unknown;
};

export type GateDecision = {
  readonly effectiveSeverity: GateSeverity;
  readonly applicable: boolean;
  readonly ceiling: GateSeverity;
  readonly reason:
    | 'ENFORCED'
    | 'RELAXED_BY_MODE'
    | 'NOT_APPLICABLE_TO_SERVICE_MODEL'
    | 'NEVER_SOFTENED'
    | 'CONFIGURED_ADVISORY'
    | 'UNREGISTERED_RULE_FAIL_CLOSED';
  readonly governingMode?: GateGoverningMode;
  readonly modeValue?: string | null;
};

/**
 * Which mode values count as enforcing. Fails CLOSED: anything unrecognised —
 * including null, a typo, or a value added later — is treated as enforcing, so
 * a configuration mistake can never silently disable a gate.
 *
 * pay_context_enforcement_mode is FOUR-valued, not binary. OFF and SHADOW are
 * both non-enforcing at publish; SHADOW means "detect and record, do not act".
 */
const ENFORCING_VALUES: Record<GateGoverningMode, readonly string[]> = {
  worker_compliance_enforcement_mode: ['ENFORCE_ROSTER', 'ENFORCE_ALL'],
  pay_context_enforcement_mode: ['ENFORCE_CONTEXT', 'ENFORCE_EXPORT'],
  roster_coverage_enforcement_mode: ['ENFORCE'],
};

const NON_ENFORCING_VALUES: Record<GateGoverningMode, readonly string[]> = {
  worker_compliance_enforcement_mode: ['WARN'],
  pay_context_enforcement_mode: ['OFF', 'SHADOW'],
  roster_coverage_enforcement_mode: ['WARN'],
};

function isEnforcing(mode: GateGoverningMode, raw: string | null | undefined): boolean {
  const value = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (ENFORCING_VALUES[mode].includes(value)) return true;
  if (NON_ENFORCING_VALUES[mode].includes(value)) return false;
  return true; // I4 — unknown/absent fails closed
}

function resolveServiceModel(
  candidate: GateCandidate,
  profile: TenantGateProfile,
): GateServiceModel | 'UNKNOWN' {
  if (candidate.serviceModel) return candidate.serviceModel;
  if (profile.serviceModel === 'MIXED') return 'UNKNOWN';
  return profile.serviceModel;
}

/**
 * Decide one candidate. Pure; no I/O. Never returns null — see I1.
 */
function applyConfiguredAdvisoryCeiling(
  ruleCode: string,
  ceiling: GateSeverity,
  profile: TenantGateProfile,
): { ceiling: GateSeverity; applied: boolean } {
  const configured = profile.advisoryCeilings?.[ruleCode];
  if (!configured) return { ceiling, applied: false };
  const capped = severityAtMost(ceiling, configured);
  return { ceiling: capped, applied: capped !== ceiling };
}

export function decideGate(candidate: GateCandidate, profile: TenantGateProfile): GateDecision {
  const entry = gateApplicability(candidate.ruleCode);

  // I4 — a rule we do not know about keeps whatever detection gave it.
  if (!entry) {
    return {
      effectiveSeverity: candidate.severity,
      applicable: true,
      ceiling: candidate.severity,
      reason: 'UNREGISTERED_RULE_FAIL_CLOSED',
    };
  }

  // I3 — safety rules ignore service model and mode entirely, and are FLOORED
  // rather than capped.
  //
  // This is deliberately not severityAtMost(). These codes are NON_NEGOTIABLE
  // per classifyComplianceRule and appear in
  // roster_publish_rule_is_non_overridable(), but that SQL guard only fires on
  // `severity = 'BLOCK'`. Detection can legitimately emit WARN for some of them
  // (missingWorkerComplianceSeverity yields WARN when the tenant's compliance
  // mode is WARN — roster-gate-detection.ts:1133). Capping would preserve that
  // WARN, and a non-overridable rule persisted as WARN is invisible to BOTH the
  // publish RPC and the hard-gate trigger: the protection silently evaporates.
  //
  // Flooring restores the invariant that a NON_NEGOTIABLE finding always
  // reaches publish as a blocker. Verified zero live instances across all
  // tenants at the time of the change, so no provider is newly blocked.
  if (entry.neverSoften) {
    return {
      effectiveSeverity: entry.enforcedSeverity,
      applicable: true,
      ceiling: entry.enforcedSeverity,
      reason: 'NEVER_SOFTENED',
    };
  }

  const model = resolveServiceModel(candidate, profile);
  // UNKNOWN (mixed tenant, unresolved shift) is treated as applicable —
  // failing closed rather than silently dropping a residential rule.
  const applicable = model === 'UNKNOWN' || entry.serviceModels.includes(model);

  if (!applicable) {
    // I1 — recorded at INFO, never dropped. This is what preserves the
    // override-reason feedback loop that reveals defective gates.
    return {
      effectiveSeverity: 'INFO',
      applicable: false,
      ceiling: 'INFO',
      reason: 'NOT_APPLICABLE_TO_SERVICE_MODEL',
    };
  }

  if (!entry.governingMode) {
    let ceiling = entry.enforcedSeverity;
    // Home care: mid-period unfilled visits are operational normal even when a
    // tenant left coverage on ENFORCE by mistake. Residential keeps the full
    // enforced ceiling (BLOCK under ENFORCE).
    if (candidate.ruleCode === 'UNFILLED_ROSTER_SLOT' && model === 'HOME_CARE') {
      ceiling = severityAtMost(ceiling, 'WARN');
    }
    const advisory = applyConfiguredAdvisoryCeiling(candidate.ruleCode, ceiling, profile);
    return {
      effectiveSeverity: severityAtMost(candidate.severity, advisory.ceiling),
      applicable: true,
      ceiling: advisory.ceiling,
      reason: advisory.applied ? 'CONFIGURED_ADVISORY' : 'ENFORCED',
    };
  }

  const modeValue = profile.modes[entry.governingMode] ?? null;
  const enforcing = isEnforcing(entry.governingMode, modeValue);
  let ceiling = enforcing ? entry.enforcedSeverity : entry.relaxedSeverity;
  if (candidate.ruleCode === 'UNFILLED_ROSTER_SLOT' && model === 'HOME_CARE') {
    ceiling = severityAtMost(ceiling, 'WARN');
  }
  const advisory = applyConfiguredAdvisoryCeiling(candidate.ruleCode, ceiling, profile);

  return {
    effectiveSeverity: severityAtMost(candidate.severity, advisory.ceiling),
    applicable: true,
    ceiling: advisory.ceiling,
    reason: advisory.applied ? 'CONFIGURED_ADVISORY' : enforcing ? 'ENFORCED' : 'RELAXED_BY_MODE',
    governingMode: entry.governingMode,
    modeValue,
  };
}

/**
 * Apply policy to a whole detected set. Returns the same number of items, in
 * the same order, with effectiveSeverity and the decision attached.
 */
export function applyGatePolicy<T extends GateCandidate>(
  candidates: readonly T[],
  profile: TenantGateProfile,
): Array<T & { severity: GateSeverity; policy: GateDecision }> {
  return candidates.map((candidate) => {
    const policy = decideGate(candidate, profile);
    return { ...candidate, severity: policy.effectiveSeverity, policy };
  });
}

export function parseRosterGateAdvisoryCeilings(
  value: unknown,
): Readonly<Partial<Record<string, GateSeverity>>> {
  if (value === undefined || value === null) return DEFAULT_ROSTER_GATE_ADVISORY_CEILINGS;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return Object.freeze({});
  const allowed: GateSeverity[] = ['INFO', 'WARN', 'BLOCK'];
  const parsed: Partial<Record<string, GateSeverity>> = {};
  for (const [ruleCode, ceiling] of Object.entries(value)) {
    if (typeof ceiling === 'string' && allowed.includes(ceiling as GateSeverity)) {
      parsed[ruleCode] = ceiling as GateSeverity;
    }
  }
  return Object.freeze(parsed);
}
